import moment from 'moment';
import { useEffect, useState } from 'react';
import { validatePlateFormat } from '../services/Validations';

export const EMAIL_PATTERN = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

export const DATE_FORMAT = 'YYYY-MM-DD'

export const COUNTRY_CODE_PREFIX = '+'

export const getFormatedPhoneAndCountryCode = (phone, countryPhoneCodes) => {
  if (!phone || !countryPhoneCodes || !phone.startsWith('+')) return { phone };
  
  if (phone.startsWith('+')) {
    const matchingCode = countryPhoneCodes.map(c => '+' + c.PhoneCountryCode).find(c => phone.startsWith(c));
    return { phone: phone.replace(matchingCode, '').replace(/ /g, '').trim(), phoneCountryCode: matchingCode };
  } else if (phone.startsWith('00')) {
    const matchingCode = countryPhoneCodes.map(c => '00' + c.PhoneCountryCode).find(c => phone.startsWith(c));
    return { phone: phone.replace(matchingCode, '').replace(/ /g, '').trim(), phoneCountryCode: matchingCode.replace('00', COUNTRY_CODE_PREFIX) };
  }
  return { phone }
}

export const getQueryStringParams = (queryString) => {
  return queryString ?
      queryString.substring(1)
        .split('&')
        .map(p => {
          const parts = p.split('=')
          return parts && parts.length === 2 ? { [parts[0]]: parts[1] } : {}
        })
        .reduce((prev, curr) => ({ ...prev, ...curr }), {})
      : null
}

export function validPortugueseDriversLicence(number) {
  return /^[a-zA-Z]{1,2}-\d{1,7} \d{1}$/g.test(number);
}

export function isPortugalCountry(country) {
  return country === 'Portugal'
}

export const ID_CARD_DOCUMENT_TYPE = 1

export const PT_CITIZEN_CARD_EXAMPLE = '00777777 9 ZZ9'

const PT_CITIZEN_CARD_PATTERN = /^\d{8} \d [A-Z0-9]{2}\d$/

/**
 * Formats a portuguese citizen card number to '00777777 9 ZZ9':
 * 6 to 8 digits (left padded with zeros), 1 control digit,
 * 2 alphanumeric characters and 1 final control digit.
 * Accepts the number with or without spaces and in lower case.
 *
 * @param {string} number - The citizen card number
 * @return {string|null} The formatted number, or null when it can not be parsed.
 */
export function normalizePtCitizenCardNumber(number) {
  if (!number) return null
  const compactNumber = String(number).replace(/\s/g, '').toUpperCase()
  const match = /^(\d{6,8})(\d)([A-Z0-9]{2})(\d)$/.exec(compactNumber)
  if (!match) return null
  const [, digits, controlDigit, alphanumeric, finalControlDigit] = match
  return `${digits.padStart(8, '0')} ${controlDigit} ${alphanumeric}${finalControlDigit}`
}

export function isValidPtCitizenCardNumber(number) {
  return PT_CITIZEN_CARD_PATTERN.test(normalizePtCitizenCardNumber(number) || '')
}

export function capitalizeString(str) {
  return str.charAt(0).toUpperCase() + str.slice(1);
}

export function isSafetyExtra(extraId) {
  // GKIOSK-1888/1889: PE, PACKH, CBF e os packs das duas vertentes (ESSI/ESS, PPI, SPI, ESSE, PPE,
  // SPE) tambem contam como extras de seguranca. Sem PE, o cartao base de uma reserva inclusive nao
  // mostraria o Pack Basic. Consumidores: os construtores de reservationPack.extrasList.
  return [
    'TP', 'CDW', 'ACDW', 'ACDW1', 'ASTOP', 'PAI', 'CBF', 'RF', 'RF1', 'RF2', 'IF', 'IF1', 'IF2', 'IF3',
    'PE', 'PACKH', 'ESSI', 'ESS', 'PPI', 'SPI', 'ESSE', 'PPE', 'SPE'
  ].includes(extraId)
}

export function padZero(value) {
  if(!value || isNaN(parseInt(value))) return value
  return Number(value) < 10 ? `0${Number(value)}` : value
}

export const PORTUGAL_COUNTRY_ID = '33'

export function isPortugalCountryId(id) { return id === PORTUGAL_COUNTRY_ID }

export function buildFieldsValidationErrors(validationsArray) {
  return validationsArray.filter(v => !v.isValid)
}

export const isSafari = () => /^((?!chrome|android).)*safari/i.test(navigator.userAgent)

export function useUrlQuery() {
  return new URLSearchParams(window.location.search)
}

export function fileDownload(data, filename, mime, bom) {
  var blobData = (typeof bom !== 'undefined') ? [bom, data] : [data]
  var blob = new Blob(blobData, {type: mime || 'application/octet-stream'});
  if (typeof window.navigator.msSaveBlob !== 'undefined') {
      // IE workaround for "HTML7007: One or more blob URLs were
      // revoked by closing the blob for which they were created.
      // These URLs will no longer resolve as the data backing
      // the URL has been freed."
      window.navigator.msSaveBlob(blob, filename);
  }
  else {
      var blobURL = (window.URL && window.URL.createObjectURL) ? window.URL.createObjectURL(blob) : window.webkitURL.createObjectURL(blob);
      var tempLink = document.createElement('a');
      tempLink.style.display = 'none';
      tempLink.href = blobURL;
      tempLink.setAttribute('download', filename);

      // Safari thinks _blank anchor are pop ups. We only want to set _blank
      // target if the browser does not support the HTML5 download attribute.
      // This allows you to download files in desktop safari if pop up blocking
      // is enabled.
      if (typeof tempLink.download === 'undefined') {
          tempLink.setAttribute('target', '_blank');
      }

      document.body.appendChild(tempLink);
      tempLink.click();

      // Fixes "webkit blob resource error 1"
      setTimeout(function() {
          document.body.removeChild(tempLink);
          window.URL.revokeObjectURL(blobURL);
      }, 200)
  }
}

export function hasAtLeastXDigits(str, x) {
  const digitPattern = /\d/g;
  const matches = str.match(digitPattern);
  return matches && matches.length >= x;
}

export function isTestsEnv() {
  return process.env.REACT_APP_WEB_API_URL.includes(':9443') 
    || process.env.REACT_APP_WEB_API_URL.includes(':8443') 
}

export function getLocalTimeZone(stationId) {
  if (!stationId) {
    try {
      const stored = localStorage.getItem('kioskCurrentStationId');
      if (stored) stationId = JSON.parse(stored).stationId;
    } catch (e) { /* fallback to default */ }
  }
  return String(stationId) === '91' ? 'Atlantic/Azores' : 'Europe/Lisbon';
}

export function useDebouncedValue(value, delay) {
  const [debouncedValue, setDebouncedValue] = useState(value)
  useEffect(
    () => {
      const handler = setTimeout(() => {
        setDebouncedValue(value)
      }, delay)
      return () => {
        clearTimeout(handler)
      }
    },
    [value, delay]
  )
  return debouncedValue
}

export function buildMomentFromDateString(date) {
  if (!date) return moment()
  return moment(date, "YYYY-MM-DD");
}

export async function isValidPlateNumber(plate) {
  const isCorrectSize = plate && ((plate.length === 6 && !plate.includes('-')) || (plate.length === 8 && plate.split('-').length - 1 === 2))
  if(!isCorrectSize) return false;
  const normalizedPlate = plate.replace(/-/g, '').toUpperCase();
  const patterns = [
    /^[A-Z]{2}[0-9]{2}[A-Z]{2}$/,
    /^[0-9]{2}[A-Z]{2}[0-9]{2}$/,
    /^[0-9]{2}[0-9]{2}[A-Z]{2}$/,
    /^[A-Z]{2}[0-9]{2}[0-9]{2}$/,
  ];
  return patterns.some((regex) => regex.test(normalizedPlate));
}

export function isElectric(fuel) {
  return fuel?.toLowerCase() === 'electric';
}
